import { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { resetPassword } from "../api";
import Headline from "../Components/Elements/Headline";
import BtnPrimaryM from "../Components/Buttons/BtnPrimaryM";
import InputText from "../Components/Inputs/InputText";
import Toast from "../Components/Elements/Toast";
import { useLanguage } from "../context/LanguageContext";

const ResetPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { copy } = useLanguage();

  const [toast, setToast] = useState({ message: "", type: "success" });
  const [isTokenValid, setIsTokenValid] = useState(true);

  // Inline Zod schema
  const resetPasswordSchema = z
    .object({
      password: z
        .string()
        .min(12, copy.reset_password.form.password.validation.min_length)
        .regex(/[A-Z]/, copy.reset_password.form.password.validation.uppercase)
        .regex(/[a-z]/, copy.reset_password.form.password.validation.lowercase)
        .regex(/[0-9]/, copy.reset_password.form.password.validation.number)
        .regex(
          /[^A-Za-z0-9]/,
          copy.reset_password.form.password.validation.special
        ),
      confirmPassword: z
        .string()
        .min(1, copy.reset_password.form.confirm_password.validation.required),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: copy.reset_password.form.confirm_password.validation.match,
      path: ["confirmPassword"],
    });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(resetPasswordSchema),
    mode: "onSubmit",
  });

  useEffect(() => {
    // Check if token exists
    if (!token) {
      setIsTokenValid(false);
      navigate("/forgot-password-expired");
    }
  }, [token, navigate]);

  const onSubmit = async (data) => {
    try {
      const response = await resetPassword(token, data.password);
      if (response.redirect) {
        // If we get a redirect URL, navigate to it
        window.location.href = response.redirect;
      } else {
        setToast({
          message: copy.reset_password.success,
          type: "success",
        });
        setTimeout(() => navigate("/login"), 3000);
      }
    } catch (error) {
      console.error("Reset password error:", error.message);
      if (error.message === copy.reset_password.error.invalid_token) {
        setIsTokenValid(false);
        navigate("/forgot-password-expired");
        return;
      }
      setToast({
        message: `${error.message}`,
        type: "error",
      });
    }
  };

  if (!isTokenValid) {
    return null; // Component will unmount as we're navigating away
  }

  return (
    <>
      {toast.message && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ message: "", type: "success" })}
        />
      )}
      <div className="w-full min-h-screen flex flex-col justify-center items-center background-page-color gap-10">
        <a
          href="https://www.niumi.dk/"
          target="_blank"
          rel="noopener noreferrer"
          className="transition-all duration-300 font-accent color-primary text-5xl"
        >
          NIUMI
        </a>
        <div className="card-base p-8 card-base-border bg-white w-[35vw] gap-10 flex flex-col">
          <Headline
            headline={copy.reset_password.headline}
            description={copy.reset_password.description}
          />
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex flex-col gap-3"
          >
            <InputText
              name="password"
              label={copy.reset_password.form.password.label}
              type="password"
              placeholder={copy.reset_password.form.password.placeholder}
              details={copy.reset_password.form.password.details}
              autoFocus
              {...register("password")}
              error={errors.password?.message}
            />
            <InputText
              name="confirmPassword"
              label={copy.reset_password.form.confirm_password.label}
              type="password"
              placeholder={
                copy.reset_password.form.confirm_password.placeholder
              }
              {...register("confirmPassword")}
              error={errors.confirmPassword?.message}
            />
            <div className="flex self-center">
              <BtnPrimaryM
                buttonCTA={copy.reset_password.form.submit}
                type="submit"
              />
            </div>
          </form>

          <div className="text-center flex gap-1 text-sm self-center">
            <p>{copy.reset_password.login.text}</p>
            <Link to="/login" className="color-primary hover:underline">
              {copy.reset_password.login.link}
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default ResetPassword;
